/**
 * CriterionText — l'affichage d'un critère, écran par écran (décision 5.2 bis, lot 8).
 *
 * Les textes arrivent DÉJÀ dans la langue du lecteur (serveur : `resolveCriterionTexts` ; écran :
 * `pickScoringLabel` / `pickBehaviorText` / `pickCriterionHelp`). Ce composant ne choisit aucune
 * langue : il met en forme.
 *
 *   <CriterionStacked>  — **Critère** en titre, *comportement* en sous-titre (cockpit, Progression, fiche collaborateur)
 *   <CriterionInline>   — **Critère** (*comportement*) (bilan de campagne, « Ma campagne »)
 *   <CriterionWorkOn>   — Faire travailler : *comportement* **(Critère)** ; `withoutLabel` : comportement seul (alerte « compétence à renforcer », Q9)
 *   <CriterionHelp>     — la définition, au SURVOL et au TOUCHER (un bouton : un doigt ne survole pas)
 *
 * Un critère sans comportement (décision 5.1) s'affiche par son seul libellé.
 */
import { useEffect, useId, useRef, useState, type CSSProperties } from "react";
import { Info } from "lucide-react";
import { useTranslation } from "react-i18next";

export interface CriterionTextProps {
  label: string;
  behavior?: string | null;
  help?: string | null;
  className?: string;
}

const net = (s: string | null | undefined) => (s ?? "").trim();

/**
 * La définition d'un critère : s'ouvre au survol (souris) ET au toucher / clic (mobile, clavier),
 * se ferme en sortant, en touchant ailleurs ou avec Échap. Rien si le critère n'a pas d'aide.
 */
export function CriterionHelp({ help, label }: { help?: string | null; label: string }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  const id = useId();
  useEffect(() => {
    if (!open) return;
    const away = (e: Event) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("pointerdown", away);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("pointerdown", away); document.removeEventListener("keydown", esc); };
  }, [open]);
  const texte = net(help);
  if (!texte) return null;
  return (
    <span ref={ref} className="relative inline-flex align-middle" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button
        type="button"
        className="-m-1.5 inline-flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1"
        aria-label={t("criterion.helpShow", { label })}
        aria-expanded={open}
        aria-describedby={open ? id : undefined}
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
      >
        <Info className="h-3.5 w-3.5" aria-hidden />
      </button>
      {open && (
        <span
          id={id}
          role="tooltip"
          className="absolute left-1/2 top-full z-50 mt-1 w-64 max-w-[calc(100vw-32px)] -translate-x-1/2 rounded-md bg-gray-800 px-3 py-2 text-left text-xs font-normal leading-snug text-white shadow-lg"
        >
          {texte}
        </span>
      )}
    </span>
  );
}

/** **Critère** en titre, *comportement* en sous-titre (manager : cockpit, Progression, fiche collaborateur). */
export function CriterionStacked({ label, behavior, help, className, titleClassName, titleStyle }: CriterionTextProps & { titleClassName?: string; titleStyle?: CSSProperties }) {
  const b = net(behavior);
  return (
    <span className={`flex min-w-0 flex-col ${className ?? ""}`}>
      <span className="flex min-w-0 items-center gap-1">
        {/* Retour à la ligne (2 lignes max), jamais tronqué sur une seule : le survol (`title`)
            n'existe pas au toucher, le texte doit se lire tel quel sur mobile. */}
        <span className={`line-clamp-2 break-words font-semibold ${titleClassName ?? ""}`} style={titleStyle} title={label}>{label}</span>
        <CriterionHelp help={help} label={label} />
      </span>
      {b && <span className="line-clamp-2 break-words text-xs italic text-muted-foreground" title={b}>{b}</span>}
    </span>
  );
}

/** **Critère** (*comportement*) — bilan de campagne, « Ma campagne ». */
export function CriterionInline({ label, behavior, help, className }: CriterionTextProps) {
  const b = net(behavior);
  return (
    <span className={className}>
      <b>{label}</b>
      {b && <> (<i>{b}</i>)</>}
      <CriterionHelp help={help} label={label} />
    </span>
  );
}

/** Faire travailler : *comportement* **(Critère)** — alertes du cockpit (la revue et l'email du lundi le reçoivent du serveur). */
export function CriterionWorkOn({ label, behavior, help, className, withoutLabel }: CriterionTextProps & {
  /**
   * Alerte « compétence à renforcer » (décision Q9, 30/09/2026) : le titre porte déjà le critère —
   * « Faire travailler : *comportement* » seul, rien quand le critère n'a pas de comportement.
   */
  withoutLabel?: boolean;
}) {
  const { t } = useTranslation();
  const b = net(behavior);
  if (withoutLabel) {
    if (!b) return null;
    return <span className={className}>{t("criterion.workOn")} <i>{b}</i><CriterionHelp help={help} label={label} /></span>;
  }
  return (
    <span className={className}>
      {t("criterion.workOn")}{" "}
      {b ? <><i>{b}</i> <b>({label})</b></> : <b>{label}</b>}
      <CriterionHelp help={help} label={label} />
    </span>
  );
}
